import { FC, ReactElement } from 'react'
import { CommandItem } from '@/components/ui/command'
import CollectionIcon from '@/components/collection-icon'
import VerifiedBadge from '@/components/verified-badge'
import { cn } from '@/lib/utils'

const CollectionItem: FC<{
	onSelect: () => void
	collection: any
	/** Type chip — e.g. "Gallery" or "Parent". */
	tag?: string
	/** Only true when the collection is genuinely verified (never auto-granted to galleries). */
	verified?: boolean
	/** Inscription the verified badge links to (the collection's gallery inscription id). */
	verifiedInscriptionId?: string
	/** Secondary line under the name (balance, inscription id, or supply). */
	secondary?: string
	/** Render the secondary line monospaced (used for inscription ids). */
	secondaryMono?: boolean
	/** First right-aligned stat column. In table mode this is Volume. */
	primaryStat?: string
	/** Second right-aligned stat column (Supply). Passing it (even "") enables fixed table columns. */
	secondaryStat?: string
}> = (props): ReactElement => {
	const {
		collection,
		onSelect,
		tag,
		verified,
		verifiedInscriptionId,
		secondary,
		secondaryMono,
		primaryStat,
		secondaryStat
	} = props
	const tableMode = secondaryStat !== undefined

	return (
		<CommandItem value={collection.id || collection.slug} onSelect={onSelect}>
			<CollectionIcon
				collection={collection}
				className="mr-2 size-8 shrink-0 rounded-full overflow-hidden"
			/>
			<div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5">
				<div className="flex min-w-0 items-center gap-1.5">
					<span className="truncate text-sm">{collection.name}</span>
					{tag && (
						<span className="shrink-0 rounded border border-border px-1.5 py-0.5 text-[10px] leading-none text-muted-foreground">
							{tag}
						</span>
					)}
					{verified === undefined ? (
						// No explicit flag (e.g. detail page) — let the badge fetch + decide by slug.
						<VerifiedBadge slug={collection.slug} className="inline-flex" />
					) : (
						verified && (
							<VerifiedBadge
								verified
								inscriptionId={verifiedInscriptionId}
								className="inline-flex"
							/>
						)
					)}
				</div>
				{secondary && (
					<p
						className={cn(
							'truncate text-muted-foreground text-xxs',
							secondaryMono && 'font-mono'
						)}
					>
						{secondary}
					</p>
				)}
			</div>
			{tableMode ? (
				<>
					<div className="ml-3 w-24 shrink-0 truncate text-right text-xxs text-muted-foreground tabular-nums">
						{primaryStat}
					</div>
					<div className="ml-2 w-16 shrink-0 truncate text-right text-xxs text-muted-foreground tabular-nums">
						{secondaryStat}
					</div>
				</>
			) : (
				primaryStat && (
					<div className="ml-3 shrink-0 whitespace-nowrap text-right text-xxs text-muted-foreground tabular-nums">
						{primaryStat}
					</div>
				)
			)}
		</CommandItem>
	)
}

export default CollectionItem
